'use client'

import { createContext, ReactNode, useCallback, useContext, useState } from 'react'

/**
 * The GraphicPreviewContext is used to store the state UI/UX state of the graphic preview.
 * To be re-used within the iframe, inspecter, toolbar, etc...
 */

export interface Box {
  top: number
  left: number
  width: number
  height: number
}

// Define the context type
interface GraphicPreviewContextType {
  // Scale state
  scale: number
  setScale: (scale: number) => void

  // Selected nodes state
  selectedNodes: Node[]
  setSelectedNodes: (elements: Node[]) => void

  // Highlight state
  highlightBoxes: Box[]
  setHighlightBoxes: (boxes: Box[]) => void

  // Text editing state
  editingNode: Node | null
  setEditingNode: (editingNode: Node | null) => void
  editingText: string
  setEditingText: (editingText: string) => void
  editingBox: Box | null
  setEditingBox: (editingBox: Box | null) => void

  // Helper functions
  clearAllStates: () => void
  exitEditMode: () => void
}

// Create the context
const GraphicPreviewContext = createContext<GraphicPreviewContextType | undefined>(undefined)

// Context provider component
interface GraphicPreviewProviderProps {
  children: ReactNode
}

export function GraphicPreviewProvider({ children }: GraphicPreviewProviderProps) {
  // Scale state
  const [scale, setScale] = useState(0.8)

  // Selected nodes state
  const [selectedNodes, setSelectedNodes] = useState<Node[]>([])

  // Highlight state
  const [highlightBoxes, setHighlightBoxes] = useState<Box[]>([])

  // Text editing state
  const [editingNode, setEditingNode] = useState<Node | null>(null)
  const [editingText, setEditingText] = useState('')
  const [editingBox, setEditingBox] = useState<Box | null>(null)

  // Helper function to clear all states (useful when loading new graphics)
  const clearAllStates = useCallback(() => {
    setSelectedNodes([])
    setHighlightBoxes([])
    setEditingNode(null)
    setEditingText('')
    setEditingBox(null)
  }, [])

  // Helper function to exit edit mode
  const exitEditMode = useCallback(() => {
    setEditingNode(null)
    setEditingText('')
    setEditingBox(null)
  }, [])

  const value: GraphicPreviewContextType = {
    // Scale state
    scale,
    setScale,

    // Selected nodes state
    selectedNodes,
    setSelectedNodes,

    // Highlight state
    highlightBoxes,
    setHighlightBoxes,

    // Text editing state
    editingNode,
    setEditingNode,
    editingText,
    setEditingText,
    editingBox,
    setEditingBox,

    // Helper functions
    clearAllStates,
    exitEditMode,
  }

  return <GraphicPreviewContext.Provider value={value}>{children}</GraphicPreviewContext.Provider>
}

// Custom hook to use the context
export function useGraphicPreview() {
  const context = useContext(GraphicPreviewContext)
  if (context === undefined) {
    throw new Error('useGraphicPreview must be used within a GraphicPreviewProvider')
  }
  return context
}
